/**
 * MAIN COMPONENT REDUX REDUCERS
 *
 * The main component reducer combines the reducers from all modules.
 *
 * @version 0.1
 */

import { combineReducers } from 'redux';
import { merge } from 'lodash';

// Import reducers from all other modules
import batchUpload from './../BatchUpload/BatchUpload.reducers';
import dashboardState from './../StartPage/StartPage.reducers';
import reviewCompanies from './../ReviewCompanies/ReviewCompanies.reducers';
import feedListing from './../FeedListing/FeedListing.reducers';
import previewCompany from './../PreviewCompany/PreviewCompany.reducers';
import header from './../../reusable/Header/Header.reducers';
import popupMessages from './../../reusable/MessagePopup/redux/MessagePopup.reducers';
import addRssFeed from './../RSSEditing/BaseFeedEditingComponent/BaseFeedEditingComponent.reducers.js';
import editThirdParty from './../../pages/EditThirdParty/EditThirdParty.reducers';
import thirdPartyPages from './../CreateNewEntity/CreateNewEntity.reducers';
import addNewCategoryModal from './../../reusable/ReusableModals/AddNewCategoryModal/AddNewCategoryModal.reducers';
import viewArticlePage from '../../reusable/PreviewArticle/ViewArticleWithNavigation.reducers';
import sanctionsWarnings from '../../containers/SanctionsWarnings/SanctionsWarnings.reducers'
import backgroundMessages from "../../reusable/BackgroundMessage/redux/BackgroundMessages.reducers";

import {initialState} from './../../reduxStore';

/**
 * GENERAL USE REDUCERS
 */

export const SET_PGUID_TRANSLATIONS = 'SET_PGUID_TRANSLATIONS';
export const SET_HELP_URL_ROOT = 'SET_HELP_URL_ROOT';
export const USER_INFO_SUCCESS = 'USER_INFO_SUCCESS';
export const USER_AUTHENTICATION_TYPE = 'USER_AUTHENTICATION_TYPE';
export const EXPERIAN_FLAG = 'EXPERIAN_FLAG';
const SHOW_SPINNER = 'SHOW_SPINNER';
const HIDE_SPINNER = 'HIDE_SPINNER';
const SHOW_FULL_SPINNER = 'SHOW_FULL_SPINNER';
const HIDE_FULL_SPINNER = 'HIDE_FULL_SPINNER';
const REMOVE_ALL_SPINNERS = 'REMOVE_ALL_SPINNERS';
const USER_LOGOUT_FAIL = 'USER_LOGOUT_FAIL';
export const USER_CHANGE_LANGUAGE = 'USER_CHANGE_LANGUAGE';
export const UPDATE_USER_PREFERENCES = 'UPDATE_USER_PREFERENCES';
export const UPDATE_PENDING_UPLOADS = 'UPDATE_PENDING_UPLOADS';
export const USER_PRODUCT_AUTHORIZATIONS = 'USER_PRODUCT_AUTHORIZATIONS';

function user(state = null, action) {

    switch (action.type) {

        case SET_PGUID_TRANSLATIONS:
            return Object.assign({}, state, {pguidTranslations: action.payload});

        case SET_HELP_URL_ROOT:
            return Object.assign({}, state, {helpUrlRoot: action.payload});

        case USER_AUTHENTICATION_TYPE:
            let newSettings = Object.assign({}, state.appSettings, action.payload);
            return Object.assign({}, state, {appSettings: newSettings});

        case EXPERIAN_FLAG:
            let newAppSettings = Object.assign({}, state.appSettings);
            newAppSettings.isExperianEnabled = action.payload;
            return Object.assign({}, state, {appSettings: newAppSettings});

        case USER_INFO_SUCCESS: {
            let prefs = merge({}, state.preferences, action.payload.preferences);

            if (action.payload.preferences &&
                action.payload.preferences.filters &&
                action.payload.preferences.filters.categoriesIdFilters &&
                !action.payload.preferences.filters.entitiesFilter
            ) {
                if (action.payload.preferences.filters.categoriesIdFilters.length === 0) {
                    prefs.filters.entitiesFilter = {
                        type: "all_entities",
                        entitiesId: [],
                        categories: {
                            allCategories: true,
                            allUnassigned: true,
                            categoriesId: []
                        }
                    };
                } else {
                    prefs.filters.entitiesFilter = {
                        type: "categories",
                        entitiesId: [],
                        categories: {
                            allCategories: false,
                            allUnassigned: false,
                            categoriesId: action.payload.preferences.filters.categoriesIdFilters
                        }
                    };
                }

            }

            return merge({}, state, action.payload, {isLoggedIn: true, preferences: prefs});
        }

        case UPDATE_USER_PREFERENCES: {
            let prefs = Object.assign({}, state.preferences, action.payload);
            return Object.assign({}, state, {isLoggedIn: true}, {preferences: prefs});
        }

        case USER_LOGOUT_FAIL:
            return Object.assign({}, state, {isLoggedIn: true}, action.payload);

        case USER_CHANGE_LANGUAGE:
            return Object.assign({}, state, {language: action.payload});

        case UPDATE_PENDING_UPLOADS:
            return Object.assign({}, state, {pendingUpload: {count: action.payload.count, uploadId: action.payload.uploadId}});

        case USER_PRODUCT_AUTHORIZATIONS:
            const entityInsightProduct = {
                name: "Nexis&#174; Entity Insight",
                url: ""
            };
            let products = action.payload;
            products.push(entityInsightProduct);
            products.sort((a, b) => (a.name > b.name) ? 1 : -1);

            return Object.assign({}, state, {productAuthorizations: products});

        default:
            return state;

    }

}

function spinners(state = false, action) {

    // Check if the specified spinner exists and if not, set it to "mainSpinner"
    let spinner        = typeof(action.payload) === 'string' && state[action.payload] ? action.payload : 'mainSpinner',
        spinnerChanges = {};

    spinnerChanges[spinner] = null;

    switch (action.type) {
        case SHOW_SPINNER:
        {
            if (spinner === 'mainSpinner') {

                /* The main spinner is the only one that has a "count" property. The count property is used so that
                 * asynchronous hide spinner commands close the spinner only in case the count decreases to 0.
                 * Thus, the issue of the loading spinner getting closed too early is prevented.
                 */
                spinnerChanges[spinner] = {
                    count: state.mainSpinner.count + 1
                };
            } else {
                spinnerChanges[spinner] = true;
            }

            return Object.assign({}, state, spinnerChanges);
        }

        case HIDE_SPINNER:
        {
            if (spinner === 'mainSpinner') {
                spinnerChanges[spinner] = {
                    count: state.mainSpinner.count === 0 ? 0 : state.mainSpinner.count - 1
                };
            } else {
                spinnerChanges[spinner] = false;
            }

            return Object.assign({}, state, spinnerChanges);
        }

        case REMOVE_ALL_SPINNERS:
            return {mainSpinner: {count: 0}, fullPageSpinner: false};

        case SHOW_FULL_SPINNER:
            return Object.assign({}, state, {fullPageSpinner: true});

        case HIDE_FULL_SPINNER:
            return Object.assign({}, state, {fullPageSpinner: false});

        default:
            return state;

    }

}

const appReducer = combineReducers(
    {
        user,
        dashboardState,
        batchUpload,
        reviewCompanies,
        feedListing,
        previewCompany,
        spinners,
        header,
        popupMessages,
        addRssFeed,
        editThirdParty,
        thirdPartyPages,
        addNewCategoryModal,
        viewArticlePage,
        sanctionsWarnings,
        backgroundMessages
    }
);

const rootReducer = (state, action) => {
    if (action.type === 'USER_LOGOUT_SUCCESS' || action.type === 'USER_INFO_FAIL') {
        let newState = Object.assign({}, initialState, {header: state.header});
        newState.user.appSettings = state.user.appSettings;
        newState.user.helpUrlRoot = state.user.helpUrlRoot;
        newState.user.lnasAuth = state.user.lnasAuth;
        state = newState;
    }
    return appReducer(state, action);
};

export default rootReducer;


// WEBPACK FOOTER //
// ./src/scripts/pages/Main/Main.reducers.js